import { useState, useEffect } from 'react';
import { Plus, Trash2, Map, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { useRotaCombo, useRotaMontagem } from '@/features/rotas/hooks/useRotas';
import { personService } from '@/features/pessoas/services/personService';
import { PessoaSearchCombobox } from '@/components/PessoaSearchCombobox';
import type { SalvarRotaMontagemPontoRequest, RotaMontagemItem } from '@/features/rotas/types';

const UFS = ['AC','AL','AM','AP','BA','CE','DF','ES','GO','MA','MG','MS','MT',
              'PA','PB','PE','PI','PR','RJ','RN','RO','RR','RS','SC','SE','SP','TO'];

const emptyPonto: SalvarRotaMontagemPontoRequest = {
  clienteId: undefined, nomeRazao: '', cidade: '', uf: '',
  bairro: '', endereco: '', numero: '', pontoEntrega: '', km: undefined,
};

const RotaMontagem = () => {
  const [rotaId, setRotaId] = useState<number | undefined>();
  const [pontoForm, setPontoForm] = useState<SalvarRotaMontagemPontoRequest>(emptyPonto);
  // lista em memória — carregada da API e modificada localmente antes de salvar
  const [listaPontos, setListaPontos] = useState<(RotaMontagemItem | SalvarRotaMontagemPontoRequest & { _tempId: number })[]>([]);

  const { rotas } = useRotaCombo();
  const { pontos, isLoading, salvarMontagem, isSalvando } = useRotaMontagem(rotaId);

  // Quando a rota muda, carrega os pontos salvos na lista local
  useEffect(() => {
    setListaPontos(pontos);
  }, [pontos]);

  // Ao selecionar um cliente, auto-preenche endereço
  const handleClienteChange = async (clienteNumId: number) => {
    try {
      const pessoa = await personService.getById(clienteNumId);
      setPontoForm(f => ({
        ...f,
        clienteId: String(clienteNumId),
        nomeRazao: pessoa.nomeRazao ?? '',
        cidade: (pessoa as any).cidade ?? (pessoa as any).enderecos?.[0]?.cidade ?? '',
        uf: (pessoa as any).uf ?? (pessoa as any).enderecos?.[0]?.uf ?? '',
        bairro: (pessoa as any).bairro ?? (pessoa as any).enderecos?.[0]?.bairro ?? '',
        endereco: (pessoa as any).logradouro ?? (pessoa as any).enderecos?.[0]?.logradouro ?? '',
        numero: (pessoa as any).numero ?? (pessoa as any).enderecos?.[0]?.numero ?? '',
      }));
    } catch {
      setPontoForm(f => ({ ...f, clienteId: String(clienteNumId) }));
    }
  };

  const handleAdicionarPonto = () => {
    if (!pontoForm.nomeRazao?.trim() && !pontoForm.clienteId) return;
    setListaPontos(prev => [...prev, { ...pontoForm, _tempId: Date.now() } as any]);
    setPontoForm(emptyPonto);
  };

  const handleRemoverPonto = (index: number) => {
    setListaPontos(prev => prev.filter((_, i) => i !== index));
  };

  const handleSalvar = () => {
    if (!rotaId) return;
    salvarMontagem({
      rotaId,
      pontos: listaPontos.map(p => ({
        clienteId:    (p as any).clienteId,
        nomeRazao:    (p as any).nomeRazao,
        cidade:       (p as any).cidade,
        cidadeId:     (p as any).cidadeId,
        uf:           (p as any).uf,
        bairro:       (p as any).bairro,
        endereco:     (p as any).endereco,
        numero:       (p as any).numero,
        pontoEntrega: (p as any).pontoEntrega,
        km:           (p as any).km,
      })),
    });
  };

  const handleAbrirMapa = () => {
    if (listaPontos.length === 0) return;
    const waypoints = listaPontos
      .map(p => {
        const parts = [
          (p as any).endereco, (p as any).numero, (p as any).bairro,
          (p as any).cidade, (p as any).uf,
        ].filter(Boolean).join(', ');
        return encodeURIComponent(parts);
      })
      .join('/');
    window.open(`https://www.google.com/maps/dir/${waypoints}`, '_blank');
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-2xl font-bold">Montagem de Rota</h1>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={handleAbrirMapa}
            disabled={listaPontos.length === 0}
          >
            <Map className="h-4 w-4 mr-2" />Abrir no Mapa
          </Button>
          <Button onClick={handleSalvar} disabled={!rotaId || isSalvando}>
            <Save className="h-4 w-4 mr-2" />
            {isSalvando ? 'Salvando...' : 'Salvar Montagem'}
          </Button>
        </div>
      </div>

      {/* Seleção da rota */}
      <div className="max-w-sm space-y-2">
        <Label>Rota *</Label>
        <Select
          value={rotaId !== undefined ? String(rotaId) : undefined}
          onValueChange={v => { setRotaId(Number(v)); setListaPontos([]); }}
        >
          <SelectTrigger>
            <SelectValue placeholder="Selecione a rota..." />
          </SelectTrigger>
          <SelectContent>
            {rotas.map(r => (
              <SelectItem key={r.id} value={r.id.toString()}>{r.descricao}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {rotaId && (
        <>
          {/* Formulário adicionar ponto */}
          <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
            <p className="text-sm font-medium">Adicionar Ponto de Entrega</p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Cliente</Label>
                <PessoaSearchCombobox
                  value={pontoForm.clienteId ? Number(pontoForm.clienteId) : undefined}
                  onChange={handleClienteChange}
                  filter="cliente"
                  placeholder="Buscar cliente..."
                />
              </div>
              <div className="space-y-2">
                <Label>Nome / Razão Social</Label>
                <Input
                  value={pontoForm.nomeRazao ?? ''}
                  onChange={e => setPontoForm(f => ({ ...f, nomeRazao: e.target.value }))}
                  placeholder="Nome"
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Endereço</Label>
                <Input
                  value={pontoForm.endereco ?? ''}
                  onChange={e => setPontoForm(f => ({ ...f, endereco: e.target.value }))}
                  placeholder="Rua, Av..."
                />
              </div>
              <div className="space-y-2">
                <Label>Número</Label>
                <Input
                  maxLength={7}
                  value={pontoForm.numero ?? ''}
                  onChange={e => setPontoForm(f => ({ ...f, numero: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Bairro</Label>
                <Input
                  value={pontoForm.bairro ?? ''}
                  onChange={e => setPontoForm(f => ({ ...f, bairro: e.target.value }))}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="space-y-2 md:col-span-2">
                <Label>Cidade</Label>
                <Input
                  value={pontoForm.cidade ?? ''}
                  onChange={e => setPontoForm(f => ({ ...f, cidade: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>UF</Label>
                <Select
                  value={pontoForm.uf ?? ''}
                  onValueChange={v => setPontoForm(f => ({ ...f, uf: v }))}
                >
                  <SelectTrigger><SelectValue placeholder="UF" /></SelectTrigger>
                  <SelectContent>
                    {UFS.map(uf => (
                      <SelectItem key={uf} value={uf}>{uf}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>KM</Label>
                <Input
                  type="number"
                  min={0}
                  value={pontoForm.km ?? ''}
                  onChange={e => setPontoForm(f => ({ ...f, km: e.target.value ? Number(e.target.value) : undefined }))}
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Ponto de Entrega / Observação</Label>
              <Input
                value={pontoForm.pontoEntrega ?? ''}
                onChange={e => setPontoForm(f => ({ ...f, pontoEntrega: e.target.value }))}
                placeholder="Ex: Entrar pelo portão lateral, deixar na portaria..."
              />
            </div>

            <div className="flex justify-end">
              <Button
                type="button"
                onClick={handleAdicionarPonto}
                disabled={!pontoForm.nomeRazao?.trim() && !pontoForm.clienteId}
              >
                <Plus className="h-4 w-4 mr-2" />Adicionar à Rota
              </Button>
            </div>
          </div>

          {/* Tabela de pontos */}
          {isLoading ? (
            <p className="text-center text-muted-foreground py-8">Carregando pontos...</p>
          ) : (
            <div className="border rounded-lg">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Cliente / Nome</TableHead>
                    <TableHead>Endereço</TableHead>
                    <TableHead>Bairro</TableHead>
                    <TableHead>Cidade</TableHead>
                    <TableHead className="w-16">UF</TableHead>
                    <TableHead>Ponto de Entrega</TableHead>
                    <TableHead className="w-20 text-right">KM</TableHead>
                    <TableHead className="w-10" />
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {listaPontos.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground py-8">
                        Nenhum ponto adicionado. Use o formulário acima para adicionar pontos à rota.
                      </TableCell>
                    </TableRow>
                  ) : listaPontos.map((p, idx) => (
                    <TableRow key={(p as any).id ?? (p as any)._tempId ?? idx}>
                      <TableCell className="font-medium">{(p as any).nomeRazao || '—'}</TableCell>
                      <TableCell>
                        {[(p as any).endereco, (p as any).numero].filter(Boolean).join(', ') || '—'}
                      </TableCell>
                      <TableCell>{(p as any).bairro || '—'}</TableCell>
                      <TableCell>{(p as any).cidade || '—'}</TableCell>
                      <TableCell>{(p as any).uf || '—'}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">
                        {(p as any).pontoEntrega || '—'}
                      </TableCell>
                      <TableCell className="text-right">
                        {(p as any).km != null ? `${(p as any).km} km` : '—'}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-7 w-7 text-muted-foreground hover:text-destructive"
                          onClick={() => handleRemoverPonto(idx)}
                        >
                          <Trash2 className="h-3 w-3" />
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default RotaMontagem;
